// web/src/components/CustomCursor.tsx
// App-wide custom cursor (motion). Position follows the pointer with ZERO lag — the x/y motion
// values are set DIRECTLY from pointermove (no spring on movement). The only animated part is the
// click gesture: a spring scale-down on pointerdown, springing back on release. Mouse (fine
// pointer) only; the OS cursor is hidden via the `has-custom-cursor` class on <html> (globals.css).
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { motion, useMotionValue, useSpring } from "motion/react";

const SIZE = 34; // rendered cursor size (px); the SVG art is 130×130
// Tip (hotspot) inside the 130 box → the image is offset so the tip lands on the true pointer.
const HOTSPOT_X = (10 / 130) * SIZE;
const HOTSPOT_Y = (10 / 130) * SIZE;

export function CustomCursor(): JSX.Element | null {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const scale = useSpring(1, { stiffness: 700, damping: 26, mass: 0.5 }); // spring — only the click scale
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia("(pointer: fine)").matches) return; // mouse only
    setEnabled(true);
    document.documentElement.classList.add("has-custom-cursor");
    const move = (e: PointerEvent): void => { x.set(e.clientX); y.set(e.clientY); }; // direct = no lag
    const down = (): void => { scale.set(0.72); }; // scale down (spring) on click
    const up = (): void => { scale.set(1); };
    window.addEventListener("pointermove", move, { passive: true });
    window.addEventListener("pointerdown", down);
    window.addEventListener("pointerup", up);
    return () => {
      document.documentElement.classList.remove("has-custom-cursor");
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerdown", down);
      window.removeEventListener("pointerup", up);
    };
  }, [x, y, scale]);

  if (!enabled) return null;

  return (
    <motion.img
      src="/cursor/arrow.svg"
      alt=""
      aria-hidden="true"
      draggable={false}
      width={SIZE}
      height={SIZE}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        x, // translate to the pointer (motion value, set directly)
        y,
        scale,
        marginLeft: -HOTSPOT_X, // shift the box so the tip sits on the pointer
        marginTop: -HOTSPOT_Y,
        transformOrigin: `${HOTSPOT_X}px ${HOTSPOT_Y}px`, // scale around the tip
        pointerEvents: "none",
        zIndex: 2147483000,
        willChange: "transform",
      }}
    />
  );
}
